import {
  AccountPlanStatus,
  BuyerJourneyOption,
  KeyRoles,
  PlaybookFilter,
  RateIndicator,
} from "./enums";

export type PaginationList<T> = {
  count: number;
  total_pages: number;
  items: T[];
};

export type BuyerJourneyType = {
  prev_type: BuyerJourneyOption;
  current_type: BuyerJourneyOption;
  last_updated: string;
};

export type AccountListItem = {
  id: string;
  index: number;
  company: string;
  industry: string;
  region: string;
  status: string;
  progress: number;
  penetration_score: number | null;
  total_engagement_score: number | null;
  employees_min: number;
  employees_max: number;
  size: string;
  is_new: boolean;
  total_user_data: number;
  total_enriched_data: number;
  scraping_status: string;
  scraping_update_status: string;
  can_be_analyzed: boolean;
  penetration_score_indicator?: RateIndicator;
  total_engagement_score_indicator?: RateIndicator;
  website_url?: string;

  discovery: {
    score: number;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
    your_data: number | null;
    external_data: number | null;
  };

  engagement: {
    score: number;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
  };
  message?: string;
  account_plan_status?: AccountPlanStatus;
  buyer_journey?: BuyerJourneyType;
  total_members?: number;
};

export type UpdatedAccountListItem = { id: string } & Partial<
  Omit<AccountListItem, "id">
>;

export type AccountList = PaginationList<AccountListItem>;

export enum Order {
  ASC = "asc",
  DESC = "desc",
}

export enum IndicatorTimeframe {
  week = "week",
  month = "month",
  quarter = "quarter ",
}

export enum TrendShift {
  growing = "growing",
  falling = "falling",
}

export type Query = {
  campaign_id?: string;
  account_id?: string;
  member_id?: string;
  page?: number;
  per_page?: number;
  sort?: string;
  order?: Order;
  search?: string;
  companies?: string[];
  industry?: string[] | string;
  region?: string[] | string;
  size?: string[] | string;
  limit?: number;
  start_date?: string;
  end_date?: string;
  list_id?: string;
  import_contacts?: boolean;
  reference_account_id?: string;
  roles?: string[];
  is_enriched?: boolean;
  min_percent?: number;
  personas?: string[];
  max_percent?: number;
  role_id?: string;
  has_files?: boolean;
  rating?: number[];
  first_import?: boolean;
  trend_shift?: TrendShift;
  indicator_timeframe?: IndicatorTimeframe;
  account_ids?: string[];
  status_filter?: AccountPlanStatus;
  include_all?: boolean;
  filter?: PlaybookFilter;
  buyer_journey?: string[];
  offset?: number;
  data_source?: string | string[];
  role?: string[];
};

export type CampaignId = { campaign_id: string };

export type ListQuery = Query & CampaignId;

export type AccountFilters = {
  industries: string[];
  regions: string[];
  sizes: string[];
  buyer_journey: string[];
  data_source: string[];
};

export type Penetration = {
  index: number;
  id: string;
  name: string;
  normalized_name: string;
  industry: string;
  region: string;
  penetration_score: number | null;
  total_engagement_score: number | null;
  region_code: string;
  total_engagement_score_indicator?: RateIndicator;
  penetration_score_indicator?: RateIndicator;
  engagement: {
    score: number | null;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
  };
  discovery: {
    score: number | null;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
  };
};

export type AnalyseList = {
  account_ids: string[];
};

export type Engagement = {
  index: number;
  id: string;
  name: string;
  normalized_name: string;
  penetration_score: number | null;
  total_engagement_score: number | null;
  total_calls: number | null;
  total_calls_responded: number | null;
  total_emails: number | null;
  total_emails_responded: number | null;
  total_meetings: number | null;
  total_engagement_score_indicator?: RateIndicator;
  penetration_score_indicator?: RateIndicator;
  engagement: {
    score: number | null;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
  };
  discovery: {
    score: number | null;
    indicator?: RateIndicator;
    changed_at?: string;
    change?: number;
  };
};

export type ActionType =
  | "create"
  | "update"
  | "connect"
  | "add-contacts"
  | "re-sync"
  | "upload"
  | "";

export type CrmTypes = "hubspot" | "salesforce";

export type AccountItem = {
  mismatch_id: string;
  account_name: string;
};

export type AccountsData = {
  created: AccountItem[];
  updated: AccountItem[];
  deleted: AccountItem[];
};

export type Contact = {
  account_id: string;
  account_name: string;
  added: number;
  updated: number;
  deleted: number;
};

export type ContentH1H5 = {
  text: string;
};

export type ContentUlList = {
  title: string;
  text: string[];
  items: string[];
  icon?: string; // can be ignored
};

export type ContentOlList = {
  title: string;
  text: string[];
};

export type ContentNoticeList = {
  title: string;
  text: string[];
};

export type ContentPersonList = {
  title: string;
  data: { title: string; text: string[] }[];
};

export type ContentBankingList = {
  title: string;
  data: { title: string; text: string[] }[];
};

export type ContentTextBlock = {
  title: string;
  text: string[];
  icon?: string;
  gap?: string;
};

type ContentKeys =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "ul-list"
  | "ol-list"
  | "notice-list"
  | "person-list"
  | "banking-list"
  | "text-block"
  | "text"
  | "topicDescription"
  | "subTopicDescription"
  | "week"
  | "trend-table"
  | "range-table"
  | "table"
  | "enrichment-signals"
  | "empty";

export type ContentTrendTable = {
  title: string;
  rows: {
    type: ContentKeys;
    value: never;
  }[][];
};

export type ContentEnrichmentSignals = {
  insights: string[];
  disclaimer: string;
  technologies_mentioned?: string[];
  da_data_date?: string;
};

export type ContentRangeTable = {
  title: string;
  rows: {
    type: ContentKeys;
    value: never;
  }[][];
};

export type Article = {
  id: string;
  isCard: boolean;
  title: string;
  text: string[];
  content: {
    type: ContentKeys;
    value: never;
  }[];
};

export type SubTopic = {
  id: string;
  title: string;
  description?: string;
  text: string[];
  article: Article[];
  playbook_url?: string;
};

export type Topic = {
  id: string;
  title: string;
  description: string;
  text: string[];
  subTopics: SubTopic[];
};

export type AccountPlan = {
  company_name: string;
  topics: Topic[];
  updated_at: string;
};

export type BubbleChartRole = {
  role: KeyRoles;
  expected: number;
  actual: number;
  deviation: number;
  weight: number;
  coverage: number;
};

export type ChartAnalytics = {
  average_coverage: number;
  bc_members_identified: number;
  roles_below_benchmark: number;
  total_contacts_below_benchmark: number;
};

export type LeadDetailsType = {
  name: string;
  company: string;
  company_start_date: string;
  title: string;
  title_start_date: string;
  linkedin: string;
  profile_description: string[];
};

export type OutboundSequenceType = {
  touch_1_connection: {
    goal: string;
    message: string;
  };
  touch_2_intro_email: {
    subject: string;
    body: string[];
  };
  touch_3_dm_followup: {
    message: string;
  };
  touch_4_use_case_email: {
    subject: string;
    body: string[];
  };
  touch_5_final_nudge: {
    voicemail: string;
    subject: string;
    body: string[];
  };
};

export type ReadFirstType = {
  description: string[]; // static text needs to add lead_company
  company_context: string[];
  keep_in_mind: string[];
  what_we_know: string[];
  common_pain_points: string[];
  what_will_help: string[];
};

export type SignalBlock = {
  disclaimer: string;
  insights: string[];
  technologies_mentioned?: string[];
};

export type PartnerEnrichmentDataAnalysisType = {
  summary: string[];
  technologies_utilized: string[];
  keys: string[];
  cloud_maturity_insight: string;
  company_overview: string[];
  current_cloud_investment_focus: string[];

  it_budget_overview: {
    cloud_environment: string;
    disclaimer: string;
    estimated_annual_it_budget: string;
  };
  key_takeaways: string[];
  intent_signals?: SignalBlock;
  install_base_insights?: SignalBlock;
  primary_cloud_drivers: string[];
  why_this_matters: string[];
};

export type OutreachResearchType = {
  linkedin_profile_analysis: {
    linkedin_profile_interests: string[];
    certifications_summary: string[];
    post_topics_summary: string[];
    technologies_mentioned: string[];
    keys_linkedin_profile: string[];
  };
  engagement_signals_analysis: {
    summary: string[];
    keys: string[];
  };
  partner_enrichment_data_analysis: PartnerEnrichmentDataAnalysisType;
  questionnaire_responses?: { question: string; answer: string }[];
  notes?: string[];
};

export type FirstCallType = {
  call_goal: string;
  call_intro: string[];
  call_contextual_hook: string[];
  call_discovery_questions: string[];
  call_value_alignment: string[];
  call_close: string[];
};

export type AmnesiaBestPracticesType = {
  summary_points: string[];
  example: string;
};

export type Playbook = {
  last_update: string;
  campaign_name: string;
  company_type: string | string[];
  lead_name: string;
  lead_company: string;
  description: string[];
  read_first?: ReadFirstType;
  do_dont: boolean;
  lead_details?: LeadDetailsType;
  outreach_research?: OutreachResearchType;
  outbound_sequence?: OutboundSequenceType;
  first_call?: FirstCallType;
  amnesia_best_practices?: AmnesiaBestPracticesType;
  crm_notes?: string[];
  is_white_label?: boolean;
};

export type GeneratePlaybookListItem = {
  id: string;
  name: string;
  member_count: number;
  members_with_playbook_count: number;
  available_for_playbook_count: number;
  has_hg_data: boolean;
};

export type TargetIndustry = {
  name: string;
  sub_industries: string[];
  is_all_sub_industries: boolean;
};

export type TargetRegion = {
  name: string;
  countries: string[];
  is_all_countries: boolean;
};

export type TargetingConfiguration = {
  target_industries: TargetIndustry[];
  target_regions: TargetRegion[];
  target_account_size: string[];
  target_revenue: string[];
  ignored_accounts_file_names: string[];
};

export type UpdatedTargetingConfiguration = CampaignId & {
  geo_list: TargetRegion[];
  size_list: string[];
  industry_list: TargetIndustry[];
  revenue_list: string[];
  reset_ignored: boolean;
  files: File[];
};
